import { MAX_ROUND_PLAYERS, ROUND_LIMITS, type RoundLimits } from "./rounds";

export const ROOM_IDS = [
	"free",
	"token",
	"stake_10",
	"stake_100",
	"stake_300",
] as const;

export type RoomId = (typeof ROOM_IDS)[number];

/**
 * The Token room (the fee room): the game token's entry is burned, and each round's prize comes from the fee pool (the
 * token's trading fees). One since 0.82: oct18b to 0.81 had a second one, Token 2 (owner decision, 19 October 2026:
 * "there can only be one token room, not two ... two is gonna be a headache to run")
 */
export const TOKEN_ROOM_IDS = ["token"] as const satisfies readonly RoomId[];
export type TokenRoomId = (typeof TOKEN_ROOM_IDS)[number];

export function isTokenRoomId(roomId: string | undefined): roomId is TokenRoomId {
	return (TOKEN_ROOM_IDS as readonly string[]).includes(roomId ?? "");
}

/**
 * the Token room's rounds: 10 players to start, as everywhere, and up to 30 of them (MAX_ROUND_PLAYERS; owner decision,
 * 19 October 2026: "Make the token fees room 30 players"). Every other room's rounds hold 10 to 25 (ROUND_LIMITS)
 */
export const TOKEN_ROUND_LIMITS: RoundLimits = Object.freeze({
	...ROUND_LIMITS,
	maxPlayers: MAX_ROUND_PLAYERS,
});

/**
 * The second Token room that oct18b to 0.81 had ("Token 2"): gone since 0.82, but a server's records (its round
 * history, the fee pool's records, the players' stats) can still name rounds played there. They were Token-room rounds:
 * their entries burned, their prizes from the one fee pool, paid in under the Token room's name (vaultRoomIdOf)
 */
export const RETIRED_TOKEN_ROOM_ID = "token_2";

/** a recorded round was played in a Token room: the Token room, or the retired second one (RETIRED_TOKEN_ROOM_ID) */
export const wasTokenRoom = (roomId: string | undefined): boolean =>
	isTokenRoomId(roomId) || roomId === RETIRED_TOKEN_ROOM_ID;

/**
 * The room as the vaults know it. A vault takes entries only for the rooms it was made with (free, token and the three
 * USDG rooms: its code checks the room's name, keccak256 of it, and burns the free and token rooms' pots). Every room
 * is its own name now; the retired second Token room (RETIRED_TOKEN_ROOM_ID) paid in under the Token room's name, so a
 * round of it still in a server's records (its vault key is its room's vault name, its id and its version) is found
 * where it was paid in. Every ticket, round key and payment check uses this, never the room id itself
 */
export function vaultRoomIdOf(roomId: string): string {
	return roomId === RETIRED_TOKEN_ROOM_ID ? "token" : roomId;
}

export const ENTRY_ASSET_IDS = ["game_token", "usdg"] as const;
export type EntryAssetId = (typeof ENTRY_ASSET_IDS)[number];

export const ROOM_CAPS = Object.freeze({
	/**
	 * the Free room and up to two paid rooms (the Token room and a USDG room: owner decisions, October 2026), each
	 * needing a game slot of its own (maxGames)
	 */
	launchEnabled: 3,
	supported: ROOM_IDS.length,
});

export type RoomEntryPolicy =
	| { readonly kind: "free" }
	| {
			readonly kind: "fixed_token";
			/** Null until an owner-approved atomic amount is configured. */
			readonly amountAtomic: string | null;
	  }
	| {
			readonly kind: "usd_quote";
			/** Display/quote target only; a payment intent freezes the token amount. */
			readonly usdCents: number;
	  };

export type RoomRewardPolicy =
	| { readonly kind: "external_fee_pool" }
	| { readonly kind: "entry_pot" };

/**
 * Immutable accounting destination for a paid entry. This is deliberately
 * separate from entryPolicy: a room may currently be free while retaining the
 * destination that applies when a later configuration makes it paid.
 */
export type RoomEntryFundsPolicy =
	| { readonly kind: "burn" }
	| { readonly kind: "winner_pot" };

export interface RoomDefinition {
	readonly id: RoomId;
	readonly displayName: string;
	readonly enabled: boolean;
	readonly sortOrder: number;
	readonly configVersion: number;
	readonly limits: RoundLimits;
	/** Stable logical asset identity; deployment configuration supplies addresses. */
	readonly entryAssetId: EntryAssetId;
	readonly entryPolicy: RoomEntryPolicy;
	readonly entryFundsPolicy: RoomEntryFundsPolicy;
	readonly rewardPolicy: RoomRewardPolicy;
}

const room = (
	definition: Omit<RoomDefinition, "limits"> & {
		readonly limits?: RoundLimits;
	},
): RoomDefinition =>
	Object.freeze({ ...definition, limits: definition.limits ?? ROUND_LIMITS });

/**
 * Safe boot catalogue. Only the free room is enabled until durable configuration,
 * coordinator routing, wallet verification, and the money gates are complete.
 */
export const DEFAULT_ROOM_DEFINITIONS: readonly RoomDefinition[] =
	Object.freeze([
		room({
			id: "free",
			displayName: "Free",
			enabled: true,
			sortOrder: 0,
			configVersion: 1,
			entryAssetId: "game_token",
			entryPolicy: { kind: "free" },
			entryFundsPolicy: { kind: "burn" },
			rewardPolicy: { kind: "external_fee_pool" },
		}),
		room({
			id: "token",
			displayName: "Token",
			enabled: false,
			sortOrder: 1,
			configVersion: 1,
			limits: TOKEN_ROUND_LIMITS,
			entryAssetId: "game_token",
			entryPolicy: { kind: "fixed_token", amountAtomic: null },
			entryFundsPolicy: { kind: "burn" },
			rewardPolicy: { kind: "external_fee_pool" },
		}),
		room({
			id: "stake_10",
			displayName: "10 USDG Entry",
			enabled: false,
			sortOrder: 2,
			configVersion: 2,
			entryAssetId: "usdg",
			entryPolicy: { kind: "fixed_token", amountAtomic: "10000000" },
			entryFundsPolicy: { kind: "winner_pot" },
			rewardPolicy: { kind: "entry_pot" },
		}),
		room({
			id: "stake_100",
			displayName: "100 USDG Entry",
			enabled: false,
			sortOrder: 3,
			configVersion: 2,
			entryAssetId: "usdg",
			entryPolicy: { kind: "fixed_token", amountAtomic: "100000000" },
			entryFundsPolicy: { kind: "winner_pot" },
			rewardPolicy: { kind: "entry_pot" },
		}),
		room({
			id: "stake_300",
			displayName: "300 USDG Entry",
			enabled: false,
			sortOrder: 4,
			configVersion: 2,
			entryAssetId: "usdg",
			entryPolicy: { kind: "fixed_token", amountAtomic: "300000000" },
			entryFundsPolicy: { kind: "winner_pot" },
			rewardPolicy: { kind: "entry_pot" },
		}),
	]);

export function isRoomId(value: unknown): value is RoomId {
	return (
		typeof value === "string" && (ROOM_IDS as readonly string[]).includes(value)
	);
}

export function isStakeRoomId(roomId: RoomId): boolean {
	return (
		roomId === "stake_10" || roomId === "stake_100" || roomId === "stake_300"
	);
}

export function validateRoomDefinitions(
	definitions: readonly RoomDefinition[],
): void {
	if (definitions.length !== ROOM_CAPS.supported) {
		throw new RangeError(
			`Room catalogue must contain exactly ${ROOM_CAPS.supported} rooms`,
		);
	}
	if (new Set(definitions.map(({ id }) => id)).size !== definitions.length) {
		throw new Error("Room IDs must be unique");
	}
	if (
		new Set(definitions.map(({ sortOrder }) => sortOrder)).size !==
		definitions.length
	) {
		throw new Error("Room sort orders must be unique");
	}
	const enabledCount = definitions.filter(({ enabled }) => enabled).length;
	if (enabledCount < 1) throw new Error("At least one room must be enabled");
	if (enabledCount > ROOM_CAPS.launchEnabled) {
		throw new Error(
			`At most ${ROOM_CAPS.launchEnabled} rooms may be enabled at launch`,
		);
	}
	for (const definition of definitions) {
		if (!isRoomId(definition.id))
			throw new Error(`Unsupported room ID: ${definition.id}`);
		if (
			!(ENTRY_ASSET_IDS as readonly string[]).includes(definition.entryAssetId)
		) {
			throw new Error(`Room ${definition.id} has an invalid entry asset`);
		}
		if (!definition.displayName.trim()) {
			throw new Error(`Room ${definition.id} needs a display name`);
		}
		if (
			!Number.isInteger(definition.sortOrder) ||
			definition.sortOrder < 0 ||
			definition.sortOrder >= ROOM_CAPS.supported
		) {
			throw new Error(`Room ${definition.id} has an invalid sort order`);
		}
		if (
			!Number.isInteger(definition.configVersion) ||
			definition.configVersion < 1
		) {
			throw new Error(
				`Room ${definition.id} has an invalid configuration version`,
			);
		}
		// (a round holds at most 25, but for the Token room's 30)
		if (
			!Number.isInteger(definition.limits.minPlayers) ||
			!Number.isInteger(definition.limits.maxPlayers) ||
			definition.limits.minPlayers < 1 ||
			definition.limits.maxPlayers >
				(isTokenRoomId(definition.id)
					? MAX_ROUND_PLAYERS
					: ROUND_LIMITS.maxPlayers) ||
			definition.limits.minPlayers > definition.limits.maxPlayers
		) {
			throw new Error(`Room ${definition.id} has invalid player limits`);
		}
		if (
			definition.entryPolicy.kind === "fixed_token" &&
			definition.entryPolicy.amountAtomic !== null &&
			!/^\d+$/u.test(definition.entryPolicy.amountAtomic)
		) {
			throw new Error(
				`Room ${definition.id} has an invalid atomic token amount`,
			);
		}
		if (
			definition.entryPolicy.kind === "usd_quote" &&
			(!Number.isSafeInteger(definition.entryPolicy.usdCents) ||
				definition.entryPolicy.usdCents < 1)
		) {
			throw new Error(`Room ${definition.id} has an invalid USD-cent target`);
		}
		if (
			definition.entryPolicy.kind !== "free" &&
			definition.entryPolicy.kind !== "fixed_token" &&
			definition.entryPolicy.kind !== "usd_quote"
		) {
			throw new Error(`Room ${definition.id} has an unknown entry policy`);
		}
		if (
			definition.entryFundsPolicy.kind !== "burn" &&
			definition.entryFundsPolicy.kind !== "winner_pot"
		) {
			throw new Error(
				`Room ${definition.id} has an unknown entry-funds policy`,
			);
		}
		if (
			definition.rewardPolicy.kind !== "external_fee_pool" &&
			definition.rewardPolicy.kind !== "entry_pot"
		) {
			throw new Error(`Room ${definition.id} has an unknown reward policy`);
		}
		if (
			definition.rewardPolicy.kind === "entry_pot" &&
			(definition.entryPolicy.kind === "free" ||
				definition.entryFundsPolicy.kind !== "winner_pot")
		) {
			throw new Error(
				`Room ${definition.id} cannot fund an entry pot with its entry policy`,
			);
		}
		const isLaunchRoom = definition.id === "free" || isTokenRoomId(definition.id);
		if (
			isLaunchRoom &&
			(definition.entryFundsPolicy.kind !== "burn" ||
				definition.rewardPolicy.kind !== "external_fee_pool")
		) {
			throw new Error(
				`Launch room ${definition.id} must burn paid entries and use external rewards`,
			);
		}
		if (isLaunchRoom && definition.entryPolicy.kind === "usd_quote") {
			throw new Error(
				`Launch room ${definition.id} must use free or fixed-token entry`,
			);
		}
		if (
			!isLaunchRoom &&
			(definition.entryAssetId !== "usdg" ||
				definition.entryFundsPolicy.kind !== "winner_pot" ||
				definition.rewardPolicy.kind !== "entry_pot" ||
				definition.entryPolicy.kind !== "fixed_token" ||
				definition.entryPolicy.amountAtomic === null)
		) {
			throw new Error(
				`Stake room ${definition.id} must require fixed USDG and route entries to its winner pot`,
			);
		}
		if (isLaunchRoom && definition.entryAssetId !== "game_token") {
			throw new Error(`Launch room ${definition.id} must use the game token`);
		}
		if (
			definition.enabled &&
			definition.entryPolicy.kind === "fixed_token" &&
			(definition.entryPolicy.amountAtomic === null ||
				BigInt(definition.entryPolicy.amountAtomic) < 1n)
		) {
			throw new Error(
				`Enabled room ${definition.id} needs a positive atomic token amount`,
			);
		}
	}
	// (the Free room and the Token room take the game token at one price)
	const paidLaunchAmounts = definitions.flatMap((definition) =>
		(definition.id === "free" || isTokenRoomId(definition.id)) &&
		definition.entryPolicy.kind === "fixed_token" &&
		definition.entryPolicy.amountAtomic !== null
			? [definition.entryPolicy.amountAtomic]
			: [],
	);
	if (new Set(paidLaunchAmounts).size > 1) {
		throw new Error("Paid launch rooms must use the same atomic token amount");
	}
}

validateRoomDefinitions(DEFAULT_ROOM_DEFINITIONS);

export const DEFAULT_ROOM = DEFAULT_ROOM_DEFINITIONS[0];
